/**
 * Spellcheck Service using nspell with local dictionaries
 *
 * This service provides spellchecking functionality using hunspell dictionaries
 * bundled locally (not fetched from remote URLs at runtime).
 */

import nspell from 'nspell';

class SpellcheckService {
    constructor() {
        this.spellchecker = null;
        this.isLoading = false;
        this.isReady = false;
        this.loadPromise = null;
        this.technicalDictionaries = new Set();
        this.technicalDictsLoaded = false;
        this.onDictionaryLoadedCallbacks = [];
        this.customDictionary = new Set();
        this._loadCustomDictionary();
    }

    /**
     * Load custom dictionary from localStorage
     * @private
     */
    _loadCustomDictionary() {
        try {
            const stored = localStorage.getItem('spellcheck_custom_dictionary');
            if (stored) {
                const words = JSON.parse(stored);
                this.customDictionary = new Set(words);
            }
        } catch (error) {
            console.warn('[SpellcheckService] Failed to load custom dictionary:', error);
        }
    }

    /**
     * Save custom dictionary to localStorage
     * @private
     */
    _saveCustomDictionary() {
        try {
            localStorage.setItem('spellcheck_custom_dictionary', JSON.stringify([...this.customDictionary]));
        } catch (error) {
            console.warn('[SpellcheckService] Failed to save custom dictionary:', error);
        }
    }

    /**
     * Initialize the spellchecker by loading dictionary files
     * @returns {Promise<void>}
     */
    async initialize() {
        if (this.isReady && this.technicalDictsLoaded) {
            return;
        }

        if (this.loadPromise) {
            await this.loadPromise;
            return;
        }

        this.isLoading = true;
        this.loadPromise = this._loadDictionaries();

        try {
            await this.loadPromise;
            this.isReady = true;
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Load dictionary files from local public directory
     * @private
     */
    async _loadDictionaries() {
        try {
            // Load dictionary files from local public directory
            const [affResponse, dicResponse] = await Promise.all([
                fetch('/dictionaries/en-US.aff'),
                fetch('/dictionaries/en-US.dic')
            ]);

            if (!affResponse.ok || !dicResponse.ok) {
                throw new Error('Failed to load dictionary files');
            }

            const [affBuffer, dicBuffer] = await Promise.all([
                affResponse.text(),
                dicResponse.text()
            ]);

            // Initialize nspell with the dictionary data
            this.spellchecker = nspell({
                aff: affBuffer,
                dic: dicBuffer
            });

            console.log('[SpellcheckService] Dictionary loaded successfully');

            // Load technical dictionaries after a delay to avoid blocking initial page load
            setTimeout(() => {
                this._loadTechnicalDictionaries().catch(err => {
                    console.error('[SpellcheckService] Failed to load technical dictionaries:', err);
                });
            }, 2000); // 2 second delay to let UI become responsive first
        } catch (error) {
            console.error('[SpellcheckService] Failed to load dictionaries:', error);
            throw error;
        }
    }

    /**
     * Load technical dictionaries and add them to the spellchecker
     * Uses Web Worker to avoid blocking the main thread
     * @private
     */
    async _loadTechnicalDictionaries() {
        const technicalDicts = [
            'medicine.txt',
            'programming.txt',
            'physics.txt',
            'biology_mesh.txt',
            'engineering.txt'
        ];

        try {
            // Create Web Worker for processing
            const worker = new Worker('/workers/spellcheck-worker.js');

            // Process each dictionary
            const processPromises = technicalDicts.map(dict => {
                return fetch(`/dictionaries/${dict}`)
                    .then(r => r.ok ? r.text() : null)
                    .then(text => {
                        if (!text) return null;

                        // Send dictionary to worker for processing
                        return new Promise((resolve, reject) => {
                            const handleMessage = (e) => {
                                const { status, dictionaryName, words, originalWordCount, error } = e.data;

                                if (dictionaryName !== dict) return; // Not our message

                                // Remove this listener
                                worker.removeEventListener('message', handleMessage);

                                if (status === 'success') {
                                    // Add words in chunks to avoid blocking the main thread
                                    const CHUNK_SIZE = 1000;
                                    let index = 0;

                                    const addChunk = () => {
                                        const end = Math.min(index + CHUNK_SIZE, words.length);

                                        for (let i = index; i < end; i++) {
                                            this.spellchecker.add(words[i]);
                                            this.technicalDictionaries.add(words[i].toLowerCase());
                                        }

                                        index = end;

                                        if (index < words.length) {
                                            // Schedule next chunk using setTimeout to yield to UI
                                            setTimeout(addChunk, 0);
                                        } else {
                                            console.log(`[SpellcheckService] Loaded ${dict}: ${originalWordCount} words (${words.length} variations)`);
                                            resolve();
                                        }
                                    };

                                    addChunk();
                                } else {
                                    console.error(`[SpellcheckService] Worker error for ${dict}:`, error);
                                    reject(new Error(error));
                                }
                            };

                            worker.addEventListener('message', handleMessage);

                            // Send dictionary to worker
                            worker.postMessage({
                                action: 'processDictionary',
                                data: {
                                    dictionaryText: text,
                                    dictionaryName: dict
                                }
                            });
                        });
                    });
            });

            // Wait for all dictionaries to be processed
            const results = await Promise.allSettled(processPromises);

            // Terminate worker after all processing is done
            worker.terminate();

            const loaded = results.filter(r => r.status === 'fulfilled').length;
            console.log(`[SpellcheckService] Technical dictionaries loaded: ${loaded}/${technicalDicts.length}`);
            this.technicalDictsLoaded = true;

            // Load custom dictionary words into spellchecker
            this.customDictionary.forEach(word => {
                this.spellchecker.add(word);
            });
            console.log(`[SpellcheckService] Loaded ${this.customDictionary.size} custom words`);

            // Notify all registered callbacks that dictionaries are loaded
            console.log(`[SpellcheckService] Notifying ${this.onDictionaryLoadedCallbacks.length} callbacks`);
            this.onDictionaryLoadedCallbacks.forEach(callback => {
                try {
                    callback();
                } catch (error) {
                    console.error('[SpellcheckService] Error in dictionary loaded callback:', error);
                }
            });
        } catch (error) {
            console.warn('[SpellcheckService] Failed to load some technical dictionaries:', error);
            this.technicalDictsLoaded = true; // Mark as loaded even on error to avoid blocking
        }
    }

    /**
     * Register a callback to be called when dictionaries are fully loaded
     * @param {Function} callback - The callback function
     */
    onDictionaryLoaded(callback) {
        console.log('[SpellcheckService] onDictionaryLoaded called, technicalDictsLoaded:', this.technicalDictsLoaded);
        if (this.technicalDictsLoaded) {
            // If already loaded, call immediately
            console.log('[SpellcheckService] Dictionary already loaded, calling callback immediately');
            try {
                console.log('[SpellcheckService] About to execute callback');
                callback();
                console.log('[SpellcheckService] Callback executed successfully');
            } catch (error) {
                console.error('[SpellcheckService] Error in immediate callback:', error);
            }
        } else {
            // Otherwise, register for later
            console.log('[SpellcheckService] Registering callback for later');
            this.onDictionaryLoadedCallbacks.push(callback);
        }
    }

    /**
     * Check if a word is spelled correctly
     * @param {string} word - The word to check
     * @returns {boolean} - True if the word is spelled correctly
     */
    correct(word) {
        if (!this.isReady || !this.spellchecker) {
            console.log(`[SpellcheckService] correct("${word}"): not ready (isReady=${this.isReady}, spellchecker=${!!this.spellchecker})`);
            return true; // Assume correct if not initialized
        }

        // Skip checking single letters and numbers
        if (word.length <= 1 || /^\d+$/.test(word)) {
            return true;
        }

        // Check if word is in technical dictionaries (case-insensitive)
        const lowerWord = word.toLowerCase();
        if (this.technicalDictionaries.has(lowerWord)) {
            return true;
        }

        const result = this.spellchecker.correct(word);

        // Debug logging for specific words
        if (lowerWord.includes('chlamid') || lowerWord.includes('chlanid')) {
            console.log(`[SpellcheckService] Checking "${word}": inTechDict=${this.technicalDictionaries.has(lowerWord)}, nspellCorrect=${result}`);
        }

        return result;
    }

    /**
     * Get spelling suggestions for a misspelled word
     * @param {string} word - The misspelled word
     * @returns {string[]} - Array of suggested corrections
     */
    suggest(word) {
        if (!this.isReady || !this.spellchecker) {
            return [];
        }

        return this.spellchecker.suggest(word);
    }

    /**
     * Add a word to the personal dictionary
     * @param {string} word - The word to add
     */
    addWord(word) {
        if (!this.isReady || !this.spellchecker) {
            return;
        }

        this.spellchecker.add(word);
        this.customDictionary.add(word);
        this._saveCustomDictionary();
    }

    /**
     * Remove a word from the personal dictionary
     * @param {string} word - The word to remove
     */
    removeWord(word) {
        if (!this.isReady || !this.spellchecker) {
            return;
        }

        this.spellchecker.remove(word);
        this.customDictionary.delete(word);
        this._saveCustomDictionary();
    }

    /**
     * Clear the entire custom dictionary
     */
    clearCustomDictionary() {
        this.customDictionary.clear();
        try {
            localStorage.removeItem('spellcheck_custom_dictionary');
        } catch (error) {
            console.warn('[SpellcheckService] Failed to clear custom dictionary from localStorage:', error);
        }
    }

    /**
     * Check if the service is ready to use
     * @returns {boolean}
     */
    isInitialized() {
        return this.isReady && this.technicalDictsLoaded;
    }
}

// Export singleton instance
export const spellcheckService = new SpellcheckService();
